import { useEffect, useState } from 'react';
import { Wifi, WifiOff, AlertTriangle, RefreshCw } from 'lucide-react';
import { useSyncStore } from '../stores/syncStore';

function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

/**
 * Persistent connectivity + sync indicator for the app shell — surfaces whether
 * the user is offline and whether any edits are pending/conflicting, so the
 * offline-first promise is visible (MYSKUX-085, Nielsen #1).
 */
export function ConnectionStatus() {
  const online = useOnline();
  const statusByEntity = useSyncStore((s) => s.statusByEntity);
  const conflicts = useSyncStore((s) => s.conflicts);

  const statuses = Object.values(statusByEntity);
  const hasConflict = conflicts.length > 0 || statuses.includes('conflict');
  const pending = statuses.some((v) => v === 'savedLocal' || v === 'syncing');

  let tone = 'success';
  let label = 'Online';
  let icon = <Wifi size={12} aria-hidden="true" />;
  let syncing = false;
  if (!online) { tone = 'neutral'; label = 'Offline'; icon = <WifiOff size={12} aria-hidden="true" />; }
  else if (hasConflict) { tone = 'danger'; label = 'Conflict'; icon = <AlertTriangle size={12} aria-hidden="true" />; }
  else if (pending) { tone = 'warn'; label = 'Syncing\u2026'; icon = <RefreshCw size={12} aria-hidden="true" />; syncing = true; }

  return (
    <output role="status" aria-label="Connection status" className={`chip chip-${tone}${syncing ? ' chip-syncing' : ''} flex-row-gap1-center`}>
      {/* OLD CODE — <span aria-hidden="true" className="flex-row-gap1-center">{icon}</span> */}
      {/* NEW CODE — green signal/Wi-Fi icon only while Online (Offline/Syncing/Conflict keep default styling) */}
      <span aria-hidden="true" className={`flex-row-gap1-center${tone === 'success' ? ' chip-icon-online' : ''}`}>{icon}</span>
      {label}
    </output>
  );
}
